/**
 * Normalizers that convert backend field metadata shapes into
 * the canonical `FormFieldMeta` contract.
 *
 * Each backend endpoint returns a different field shape. These
 * functions are the ONLY place where that mapping happens.
 * Pages never manually reconstruct field metadata.
 *
 * Supported sources:
 * - Registration/holding metadata (FieldMeta from products.ts)
 * - Claim action metadata (ActionFieldMeta)
 * - Host/transaction template attributes
 */

import { titleCaseKey } from "@/shared/lib/humanise";

import type {
  FormFieldMeta,
  FieldType,
  OptionSeverity,
  SelectOption,
  ValidationRule,
} from "./types";

// ── Registration / Holding fields ───────────────────────────

/**
 * Normalise admin `FieldMeta` from registration/holding metadata.
 *
 * Source: `GET /registrations/metadata/form-fields/` → `RegistrationFormMetadata.fields`
 * Type: `import type { FieldMeta } from '@/admin/types/products'`
 *
 * Preserves the three-level required semantics (required, required_to_save,
 * required_to_publish) so the form system can gate differently for
 * draft saves vs. publishes.
 */
export function normaliseRegistrationField(field: {
  uuid?: string;
  code_name: string;
  display_name: string;
  field_type: string;
  required?: boolean;
  required_to_publish?: boolean;
  required_to_save?: boolean;
  hint?: string;
  description?: string;
  options?: SelectOption[];
  validation_rules?: ValidationRule[];
  order?: number;
  attribute_level?: string;
  maps_to_column?: string;
  visibility?: string;
  configuration_data?: Record<string, unknown>;
}): FormFieldMeta {
  const options =
    field.options ?? resolveConfigOptions(field.configuration_data);

  return {
    code_name: field.code_name,
    display_name: field.display_name,
    field_type: field.field_type as FieldType,
    required: field.required_to_publish ?? field.required ?? false,
    required_to_save: field.required_to_save,
    required_to_publish: field.required_to_publish,
    hint: field.hint || undefined,
    description: field.description || undefined,
    options,
    validation_rules: field.validation_rules,
    order: field.order ?? 0,
    configuration_data: field.configuration_data,
    source_uuid: field.uuid,
    attribute_level: field.attribute_level,
    maps_to_column: field.maps_to_column,
    visibility: field.visibility,
  };
}

// ── Claim fields ────────────────────────────────────────────

/**
 * Normalise claim action field metadata.
 *
 * Source: `GET /holdings/{uuid}/claim-metadata/` → `{ fields }`
 *         `GET /claims/claim-workflows/create-claim/metadata/` → `{ fields }`
 */
export function normaliseClaimField(field: {
  code_name: string;
  display_name: string;
  field_type: string;
  required: boolean;
  hint?: string;
  description?: string;
  configuration_data?: Record<string, unknown>;
}): FormFieldMeta {
  const options = resolveConfigOptions(field.configuration_data);
  const fileUseCase = field.configuration_data?.file_use_case;
  const fileMultiple = field.configuration_data?.file_multiple;

  return {
    code_name: field.code_name,
    display_name: field.display_name,
    field_type: field.field_type as FieldType,
    required: field.required,
    hint: field.hint || undefined,
    description: field.description || undefined,
    options,
    configuration_data: field.configuration_data,
    file_use_case: typeof fileUseCase === "string" ? fileUseCase : undefined,
    file_multiple: typeof fileMultiple === "boolean" ? fileMultiple : undefined,
  };
}

// ── Inspection report fields ────────────────────────────────

/**
 * Normalise an inspection-report field into the canonical contract.
 *
 * Source: `GET /inspections/{uuid}/report-metadata/` -> `{ fields }`
 *
 * The report's MULTI_SELECT sections carry a per-option `severity` inside
 * `configuration_data.options[*]`; `resolveConfigOptions` preserves it so
 * the generated multi-select can chip red/amber findings. NUMBER and
 * LONG_TEXT fields have no options. `group` is passed through for layout
 * grouping. Fields arrive pre-sorted by `order`.
 */
export function normaliseInspectionReportField(field: {
  code_name: string;
  display_name: string;
  field_type: string;
  required?: boolean;
  order?: number;
  description?: string;
  configuration_data?: Record<string, unknown>;
}): FormFieldMeta {
  const options = resolveConfigOptions(field.configuration_data);
  const group =
    typeof field.configuration_data?.group === "string"
      ? (field.configuration_data.group as string)
      : undefined;

  return {
    code_name: field.code_name,
    display_name: field.display_name,
    field_type: field.field_type as FieldType,
    required: field.required ?? false,
    description: field.description || undefined,
    options,
    order: field.order ?? 0,
    group,
    configuration_data: field.configuration_data,
  };
}

// ── Host / Transaction template attributes ──────────────────

/**
 * Normalise host or transaction template attribute metadata.
 *
 * Source: `HostTemplate.attributes` or `TransactionTemplate.attributes`
 */
export function normaliseTemplateAttribute(attr: {
  uuid?: string;
  code_name: string;
  display_name: string;
  field_type: string;
  required?: boolean;
  hint?: string;
  description?: string;
  order?: number;
  maps_to_column?: string;
  configuration_data?: Record<string, unknown>;
}): FormFieldMeta {
  const options = resolveConfigOptions(attr.configuration_data);
  const fileUseCase = attr.configuration_data?.file_use_case;
  const fileMultiple = attr.configuration_data?.file_multiple;

  return {
    code_name: attr.code_name,
    display_name: attr.display_name,
    field_type: attr.field_type as FieldType,
    required: attr.required ?? false,
    hint: attr.hint || undefined,
    description: attr.description || undefined,
    options,
    order: attr.order ?? 0,
    configuration_data: attr.configuration_data,
    source_uuid: attr.uuid,
    maps_to_column: attr.maps_to_column,
    file_use_case: typeof fileUseCase === "string" ? fileUseCase : undefined,
    file_multiple: typeof fileMultiple === "boolean" ? fileMultiple : undefined,
  };
}

// ── Editable field metadata (claim detail) ──────────────────

/**
 * Normalise editable field metadata from claim detail responses.
 *
 * Source: `claim.metadata.editable_field_metadata`
 */
export function normaliseEditableField(field: {
  code_name: string;
  display_name: string;
  field_type: string;
  required?: boolean;
  hint?: string;
  options?: SelectOption[];
  configuration_data?: Record<string, unknown>;
}): FormFieldMeta {
  const options =
    field.options ?? resolveConfigOptions(field.configuration_data);

  return {
    code_name: field.code_name,
    display_name: field.display_name,
    field_type: field.field_type as FieldType,
    required: field.required ?? false,
    hint: field.hint || undefined,
    options,
    configuration_data: field.configuration_data,
  };
}

// ── Helpers ──────────────────────────────────────────────────

/**
 * Extract SELECT/DROPDOWN options from `configuration_data.options`.
 *
 * Backend stores options in `configuration_data` for some field types.
 * This is the single place where that extraction happens.
 */
export function resolveConfigOptions(
  configData?: Record<string, unknown>,
): SelectOption[] | undefined {
  if (!configData?.options || !Array.isArray(configData.options))
    return undefined;
  // Options are normally `{ value, label }` objects, but some seeds store a
  // bare string per option. Accept both so a single malformed option can't
  // crash the whole form via the error boundary (the entire panel renders
  // through this one map). A non-string, value-less object is skipped.
  return (configData.options as Array<unknown>)
    .map((opt): SelectOption | null => {
      if (typeof opt === "string") {
        return { value: opt, label: titleCaseKey(opt) };
      }
      if (opt && typeof opt === "object") {
        const { value, label, severity } = opt as {
          value?: unknown;
          label?: unknown;
          severity?: unknown;
        };
        const valueStr =
          typeof value === "string"
            ? value
            : value != null
              ? String(value)
              : undefined;
        if (valueStr === undefined) return null;
        const labelStr = typeof label === "string" ? label : undefined;
        // Preserve a per-option severity when the backend supplies one
        // (checklist-style findings). Anything outside the known set is
        // dropped so a malformed value can't reach the renderer.
        const severityVal =
          severity === "red" || severity === "yellow" || severity === "none"
            ? (severity as OptionSeverity)
            : undefined;
        return {
          value: valueStr,
          label: labelStr || titleCaseKey(valueStr),
          ...(severityVal ? { severity: severityVal } : {}),
        };
      }
      return null;
    })
    .filter((opt): opt is SelectOption => opt !== null);
}


// ── Cascading field helpers ─────────────────────────────────

function toSelectOptions(items: unknown): SelectOption[] | undefined {
  if (!Array.isArray(items)) return undefined;
  return (items as Array<{ value: string; label?: string }>).map((opt) => ({
    value: opt.value,
    label:
      opt.label || titleCaseKey(opt.value),
  }));
}

/**
 * Resolve SELECT options from `configuration_data` that may use
 * `depends_on` / `option_groups` for cascading behaviour.
 *
 * Recursively walks nested `depends_on` structures (e.g., product_impacted
 * → component_type → defect list). Falls back to flat `options` when no
 * dependency is declared or the parent has no value.
 */
export function resolveDependentOptions(
  configData: Record<string, unknown> | undefined,
  formValues: Record<string, unknown>,
): SelectOption[] | undefined {
  if (!configData?.depends_on) return resolveConfigOptions(configData);

  const parentField = configData.depends_on as string;
  const parentValue = formValues[parentField] as string | undefined;
  const optionGroups = configData.option_groups as
    Record<string, unknown> | undefined;

  if (!parentValue || !optionGroups) {
    return resolveConfigOptions(configData);
  }

  const group = optionGroups[parentValue];
  if (!group) return resolveConfigOptions(configData);

  if (
    typeof group === "object" &&
    !Array.isArray(group) &&
    (group as Record<string, unknown>).depends_on
  ) {
    return resolveDependentOptions(
      group as Record<string, unknown>,
      formValues,
    );
  }

  return toSelectOptions(group) ?? resolveConfigOptions(configData);
}

/**
 * Return a non-empty conditions dict for `key`, else `null`.
 *
 * Mirrors `_conditions` in the backend counterpart
 * `backend/apps/entities/field_config.py`: a missing config, a missing key, a
 * non-object value, or an *empty* object all collapse to "no condition". The
 * empty-object case matters - `!{}` is `false` in JS, so without this an empty
 * `{}` would fall through to `Object.entries({}).every(...)` (vacuously `true`)
 * and diverge from the backend, which treats empty as "no condition". The two
 * files MUST agree; see `normalise.test.ts` for the parity cases.
 */
function conditionsFor(
  configData: Record<string, unknown> | undefined,
  key: "visible_when" | "required_when",
): Record<string, unknown> | null {
  const raw = configData?.[key];
  if (
    raw == null ||
    typeof raw !== "object" ||
    Array.isArray(raw) ||
    Object.keys(raw as Record<string, unknown>).length === 0
  ) {
    return null;
  }
  return raw as Record<string, unknown>;
}

/**
 * Check whether a field should be visible given current form values.
 *
 * Reads `configuration_data.visible_when` - a dict mapping parent field
 * names to arrays of allowed values. All conditions must be met. A missing or
 * empty `visible_when` means "always visible".
 */
export function isFieldVisible(
  configData: Record<string, unknown> | undefined,
  formValues: Record<string, unknown>,
): boolean {
  if (configData?.hidden === true) return false;
  const conditions = conditionsFor(configData, "visible_when");
  if (conditions === null) return true;
  return Object.entries(conditions).every(([field, allowedValues]) => {
    if (!Array.isArray(allowedValues)) return true;
    const currentValue = formValues[field];
    return currentValue != null && allowedValues.includes(currentValue);
  });
}

/**
 * Check whether a field becomes required given current form values.
 *
 * Reads `configuration_data.required_when` - same shape as `visible_when`. A
 * missing or empty `required_when` means "not conditionally required".
 */
export function isConditionallyRequired(
  configData: Record<string, unknown> | undefined,
  formValues: Record<string, unknown>,
): boolean {
  const conditions = conditionsFor(configData, "required_when");
  if (conditions === null) return false;
  return Object.entries(conditions).every(([field, allowedValues]) => {
    if (!Array.isArray(allowedValues)) return false;
    const currentValue = formValues[field];
    return currentValue != null && allowedValues.includes(currentValue);
  });
}

/**
 * Whether a field is required right now: its static `required` flag OR a
 * satisfied `required_when` condition. Counterpart to `is_effectively_required`
 * in `backend/apps/entities/field_config.py`; the two MUST agree.
 */
export function effectiveRequired(
  configData: Record<string, unknown> | undefined,
  formValues: Record<string, unknown>,
  baseRequired: boolean,
): boolean {
  return baseRequired || isConditionallyRequired(configData, formValues);
}
